import { useEffect, useState } from "react";
import { getAuthToken, syncApiUrl } from "@cliqsuite/db/powersync";
import { Money } from "@cliqsuite/ui";
import { useRows } from "../lib/db";
import { usePosShapeSyncReady } from "./PosDbProvider";

/** What the manager signs, drawn as a ticket: a stub saying what kind, the bill, the amount. */
export interface ApprovalTicket {
  stub: string;
  icon: string;
  line: string;
  amount: number;
  note: string;
}

export interface PinApprovalModalProps {
  storeId: string;
  label: string;
  ticket?: ApprovalTicket;
  currency?: { code: string; precision: 0 | 2 | 3 };
  onCancel: () => void;
  onApproved: (staffId: string) => void | Promise<void>;
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");

/**
 * Manager-PIN gate for sensitive till actions (canvas v2-d-refund-pin): what is being approved as a ticket, the managers
 * as person keys with their colour down the side, the PIN in sunk cells and a pad of keys.
 */
export function PinApprovalModal({ storeId, label, ticket, currency, onCancel, onApproved }: PinApprovalModalProps) {
  const managersResult = useRows("staff", "SELECT * FROM staff WHERE store_id = $1 AND is_active = true AND is_manager = true", [storeId]);
  // staff's own ready signal, not the broad all-shapes one — see
  // PosDbProvider.tsx's usePosShapeSyncReady doc. Getting this wrong here
  // isn't just cosmetic: it would silently block a real refund/approval
  // action for however long the false "no managers" state lasted.
  const posSynced = usePosShapeSyncReady("staff");
  const managersLoading = !posSynced || !managersResult;
  const managers = managersResult?.rows ?? [];
  const [pickedId, setPickedId] = useState<string | null>(managers[0]?.id ?? null);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // useState's initializer above only ever runs once, at mount — if this
  // modal opened while managers were still loading (the common case, since
  // PGlite's initial sync is rarely instant), pickedId stayed null forever
  // even once the real roster arrived, silently no-opping PIN entry
  // (submit()'s `if (!picked) return`) with no error shown. Auto-picks the
  // first manager once the roster is in and nothing's been explicitly
  // chosen yet — never overrides a real tap.
  useEffect(() => {
    if (pickedId === null && managers.length > 0) setPickedId(managers[0]!.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managers]);

  const picked = managers.find((m) => m.id === pickedId) ?? null;

  async function submit(nextPin: string) {
    if (!picked || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(syncApiUrl(`/core/staff/${picked.id}/verify-pin`), {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${getAuthToken() ?? ""}` },
        body: JSON.stringify({ pin: nextPin }),
      });
      const body = (await res.json()) as { ok: boolean };
      if (body.ok) await onApproved(picked.id);
      else setError("That PIN didn't match. Try again.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Couldn't complete this action — try again.");
    } finally {
      setPin("");
      setSubmitting(false);
    }
  }

  const press = (d: string) => {
    if (submitting) return;
    const next = (pin + d).slice(0, 4);
    setPin(next);
    setError("");
    if (next.length === 4) void submit(next);
  };

  return (
    <div className="pos-dim" onClick={onCancel}>
      <section role="dialog" aria-modal="true" aria-labelledby="approve-t" className="pos-dialog pos-sign" onClick={(e) => e.stopPropagation()}>
        <div className="pos-sign-who">
          <div className="pos-sign-head">
            <span className="pos-approve-badge">
              <span className="ms" aria-hidden="true">admin_panel_settings</span>
            </span>
            <div>
              <h2 id="approve-t">A manager signs this</h2>
              <p>Pick who you are, then your PIN</p>
            </div>
          </div>
          {ticket ? (
            <div className="pos-sign-ticket">
              <div>
                <span className="pos-sign-stub">
                  <span className="ms" aria-hidden="true">{ticket.icon}</span>
                  {ticket.stub}
                </span>
                <span className="pos-sign-body">
                  <small>{ticket.line}</small>
                  <b className="num">{currency ? <Money amount={ticket.amount} opts={{ currencyCode: currency.code, precision: currency.precision }} /> : ticket.amount}</b>
                  <small>{ticket.note}</small>
                </span>
              </div>
              <div aria-hidden="true" className="pos-sign-teeth" />
            </div>
          ) : (
            <p className="pos-sign-label">{label}</p>
          )}
          {managersLoading ? (
            <p className="pos-approve-wait">Loading managers…</p>
          ) : managers.length === 0 ? (
            <p className="pos-approve-wait">No manager is on staff at this store. Add one in Owner Portal, under Staff.</p>
          ) : (
            <>
              <span className="pos-sign-cap">Managers on this register</span>
              <div role="radiogroup" aria-label="Manager" className="pos-sign-people">
                {managers.map((m) => (
                  <button
                    key={m.id}
                    role="radio"
                    aria-checked={m.id === pickedId}
                    className={m.id === pickedId ? "on" : ""}
                    style={{ ["--who" as string]: m.avatar_color || "var(--blue)" }}
                    onClick={() => {
                      setPickedId(m.id);
                      setPin("");
                      setError("");
                    }}
                  >
                    <i aria-hidden="true">{initials(m.name)}</i>
                    <span>
                      <b>{m.name}</b>
                      <small>{m.role}</small>
                    </span>
                    {m.id === pickedId && <span className="ms" aria-hidden="true">check_circle</span>}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        {managers.length > 0 && (
          <div className="pos-sign-pin">
            <span className="pos-sign-cap">{picked ? `${picked.name.split(" ")[0]}'s PIN` : "PIN"}</span>
            <div className={`pos-sign-cells${error ? " bad" : ""}`} aria-label={`${pin.length} of 4 digits entered`}>
              {[0, 1, 2, 3].map((i) => (
                <span key={i}>{i < pin.length && <i />}</span>
              ))}
            </div>
            {error && (
              <div role="alert" className="pos-field-err" style={{ justifyContent: "center" }}>
                <span className="ms" aria-hidden="true" style={{ fontSize: 16 }}>error</span>
                {error}
              </div>
            )}
            <div className="pos-sign-keys">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
                <button key={d} className="num" onClick={() => press(d)}>
                  {d}
                </button>
              ))}
              <span />
              <button className="num" onClick={() => press("0")}>
                0
              </button>
              <button aria-label="Delete" onClick={() => setPin((p) => p.slice(0, -1))}>
                <span className="ms" aria-hidden="true">backspace</span>
              </button>
            </div>
            <button className="pos-sign-cancel" onClick={onCancel}>
              Cancel
            </button>
          </div>
        )}
        {managers.length === 0 && (
          <button className="pos-sign-cancel" onClick={onCancel}>
            Cancel
          </button>
        )}
      </section>
    </div>
  );
}
